import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { format, parseISO } from "date-fns";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multiselectWithChip: {
        margin: theme.spacing(1),
        minWidth: 120,
        maxWidth: "100%",
    },
    dialogPaper: {
        minHeight: "400px",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
}));

const ScheduleSchema = Yup.object().shape({
    message: Yup.string().required("Obrigatório"),
    scheduledFor: Yup.string().required("Obrigatório"),
    contactId: Yup.number().required("Obrigatório"),
});

const ContactScheduleModal = ({ open, onClose, scheduleId, contactId: initialContactId, initialValues }) => {
    const classes = useStyles();
    const initialState = {
        message: "",
        scheduledFor: "",
        contactId: initialContactId || "",
        isRecurring: false,
        recursionInterval: "",
        recursionEndDate: "",
        messageType: "text",
        whatsappId: "",
        attachment: {
            title: "",
            footer: "",
            buttonLabel: "",
            buttons: ["", "", ""],
            listRows: ["", "", ""],
            cards: [{ title: "", description: "", mediaUrl: "", button: "" }]
        }
    };

    const [schedule, setSchedule] = useState(initialState);
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [mediaFile, setMediaFile] = useState(null);
    const [whatsapps, setWhatsapps] = useState([]);
    const [carouselCards, setCarouselCards] = useState([{ title: "", description: "", mediaUrl: "", button: "" }]);
    const [buttons, setButtons] = useState(["", "", ""]);
    const [listRows, setListRows] = useState(["", "", ""]);
    const [title, setTitle] = useState("");
    const [footer, setFooter] = useState("");
    const [buttonLabel, setButtonLabel] = useState("");

    useEffect(() => {
        const fetchWhatsapps = async () => {
            try {
                const { data } = await api.get("/whatsapp/");
                setWhatsapps(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchWhatsapps();
    }, []);

    useEffect(() => {
        if (open) {
            if (scheduleId) {
                const fetchSchedule = async () => {
                    try {
                        const { data } = await api.get(`/contact-schedules/${scheduleId}`);
                        const formattedSchedule = {
                            ...data,
                            scheduledFor: data.scheduledFor ? format(parseISO(data.scheduledFor), "yyyy-MM-dd'T'HH:mm") : "",
                            recursionEndDate: data.recursionEndDate ? data.recursionEndDate.substring(0, 10) : ""
                        };
                        setSchedule(formattedSchedule);
                        setSelectedContact(data.contact);

                        if (data.attachment) {
                            const att = typeof data.attachment === "string" ? JSON.parse(data.attachment) : data.attachment;
                            setTitle(att.title || "");
                            setFooter(att.footer || "");
                            setButtons(att.buttons || ["", "", ""]);
                            setListRows(att.listRows || ["", "", ""]);
                            setButtonLabel(att.buttonLabel || "");
                            setCarouselCards(att.cards || [{ title: "", description: "", mediaUrl: "", button: "" }]);
                        }
                    } catch (err) {
                        toastError(err);
                    }
                };
                fetchSchedule();
            } else if (initialValues) {
                const formattedSchedule = {
                    ...initialValues,
                    scheduledFor: initialValues.scheduledFor ? format(parseISO(initialValues.scheduledFor), "yyyy-MM-dd'T'HH:mm") : "",
                    recursionEndDate: initialValues.recursionEndDate ? initialValues.recursionEndDate.substring(0, 10) : ""
                };
                setSchedule(formattedSchedule);
                setSelectedContact(initialValues.contact || null);

                if (initialValues.attachment) {
                    const att = typeof initialValues.attachment === "string" ? JSON.parse(initialValues.attachment) : initialValues.attachment;
                    setTitle(att.title || "");
                    setFooter(att.footer || "");
                    setButtons(att.buttons || ["", "", ""]);
                    setListRows(att.listRows || ["", "", ""]);
                    setButtonLabel(att.buttonLabel || "");
                    setCarouselCards(att.cards || [{ title: "", description: "", mediaUrl: "", button: "" }]);
                }
            } else {
                setSchedule({ ...initialState, contactId: initialContactId || "" });
                if (initialContactId) {
                    const fetchContact = async () => {
                        try {
                            const { data } = await api.get(`/contacts/${initialContactId}`);
                            setSelectedContact(data);
                        } catch (err) {
                            toastError(err);
                        }
                    };
                    fetchContact();
                }
            }
        }
    }, [open, scheduleId, initialContactId, initialValues]);

    useEffect(() => {
        if (searchParam.length < 2) return;
        const delayDebounceFn = setTimeout(() => {
            const fetchContacts = async () => {
                try {
                    const { data } = await api.get("/contacts/", {
                        params: { searchParam },
                    });
                    setContacts(data.contacts);
                } catch (err) {
                    toastError(err);
                }
            };
            fetchContacts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam]);

    const handleClose = () => {
        onClose();
        setSchedule(initialState);
        setSelectedContact(null);
        setMediaFile(null);
        setCarouselCards([{ title: "", description: "", mediaUrl: "", button: "" }]);
    };

    const handleSaveSchedule = async (values) => {
        try {
            const formData = new FormData();
            formData.append("message", values.message);
            formData.append("scheduledFor", values.scheduledFor ? new Date(values.scheduledFor).toISOString() : "");
            formData.append("contactId", values.contactId);
            formData.append("isRecurring", values.isRecurring);
            formData.append("recursionInterval", values.recursionInterval);
            formData.append("recursionEndDate", values.recursionEndDate ? new Date(values.recursionEndDate + "T00:00:00").toISOString() : "");
            formData.append("messageType", values.messageType);
            formData.append("whatsappId", values.whatsappId);

            if (mediaFile) {
                formData.append("file", mediaFile);
            }

            // Adicionar imagens do carrossel
            if (values.messageType === "carousel") {
                carouselCards.forEach((item, index) => {
                    if (item.imageFile) {
                        formData.append(`carousel_image_${index}`, item.imageFile);
                    }
                });
            }

            // Complex types serialization
            let attachmentData = null;
            if (values.messageType === "button") {
                attachmentData = { title, footer, buttons: buttons.filter(b => b !== "") };
            } else if (values.messageType === "list") {
                attachmentData = { title, footer, buttonLabel, listRows: listRows.filter(r => r !== "") };
            } else if (values.messageType === "carousel") {
                attachmentData = {
                    cards: carouselCards.map(card => ({
                        title: card.title,
                        description: card.description,
                        button: card.button,
                        mediaUrl: card.mediaUrl || ""
                    }))
                };
            }

            if (attachmentData) {
                formData.append("attachment", JSON.stringify(attachmentData));
            }

            if (scheduleId) {
                await api.put(`/contact-schedules/${scheduleId}`, formData);
                toast.success("Agendamento atualizado com sucesso");
            } else {
                await api.post("/contact-schedules", formData);
                toast.success("Agendamento criado com sucesso");
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle>
                    {scheduleId ? "Editar Agendamento" : "Novo Agendamento"}
                </DialogTitle>
                <Formik
                    initialValues={schedule}
                    enableReinitialize={true}
                    validationSchema={ScheduleSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveSchedule(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <div style={{ marginBottom: 20 }}>
                                    <Autocomplete
                                        fullWidth
                                        freeSolo
                                        options={contacts}
                                        getOptionLabel={(option) => {
                                            if (typeof option === "string") return option;
                                            return option.number ? `${option.name} - ${option.number}` : option.name;
                                        }}
                                        value={selectedContact}
                                        onInputChange={(e, value) => setSearchParam(value)}
                                        onChange={(e, value) => {
                                            setSelectedContact(value);
                                            setFieldValue("contactId", value ? value.id : "");
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Contato"
                                                variant="outlined"
                                                error={touched.contactId && Boolean(errors.contactId) && !selectedContact}
                                                helperText={touched.contactId && errors.contactId && !selectedContact ? errors.contactId : "Digite o nome ou número"}
                                            />
                                        )}
                                    />
                                </div>

                                <Field as={TextField} label="Data e Hora" type="datetime-local" name="scheduledFor" InputLabelProps={{ shrink: true }} error={touched.scheduledFor && Boolean(errors.scheduledFor)} helperText={touched.scheduledFor && errors.scheduledFor} variant="outlined" fullWidth style={{ marginBottom: 20 }} />

                                <Field as={TextField} label="Mensagem" name="message" error={touched.message && Boolean(errors.message)} helperText={touched.message && errors.message} variant="outlined" margin="dense" fullWidth multiline rows={4} style={{ marginBottom: 20 }} />

                                <FormControl variant="outlined" fullWidth style={{ marginBottom: 20 }}>
                                    <InputLabel>Conexão (Opcional)</InputLabel>
                                    <Select value={values.whatsappId || ""} onChange={(e) => setFieldValue("whatsappId", e.target.value)} label="Conexão (Opcional)">
                                        <MenuItem value=""><em>Automático</em></MenuItem>
                                        {whatsapps.map((whatsapp) => (
                                            <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl variant="outlined" fullWidth style={{ marginBottom: 20 }}>
                                    <InputLabel>Tipo de Mensagem</InputLabel>
                                    <Select value={values.messageType} onChange={(e) => setFieldValue("messageType", e.target.value)} label="Tipo de Mensagem">
                                        <MenuItem value="text">Texto</MenuItem>
                                        <MenuItem value="image">Imagem</MenuItem>
                                        <MenuItem value="video">Vídeo</MenuItem>
                                        <MenuItem value="file">Arquivo (PDF, DOC, XLS)</MenuItem>
                                        <MenuItem value="button">Botão</MenuItem>
                                        <MenuItem value="list">Lista</MenuItem>
                                        <MenuItem value="carousel">Carrossel</MenuItem>
                                    </Select>
                                </FormControl>

                                {(values.messageType === "image" || values.messageType === "video" || values.messageType === "file") && (
                                    <div style={{ marginBottom: 20 }}>
                                        <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} fullWidth>
                                            {mediaFile ? mediaFile.name : `Upload ${values.messageType === "image" ? "Imagem" : values.messageType === "video" ? "Vídeo" : "Arquivo"}`}
                                            <input type="file" accept={values.messageType === "image" ? "image/*" : values.messageType === "video" ? "video/*" : ".pdf,.doc,.docx,.xls,.xlsx"} hidden onChange={(e) => setMediaFile(e.target.files[0])} />
                                        </Button>
                                    </div>
                                )}

                                {values.messageType === "button" && (
                                    <div style={{ marginBottom: 20 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}><TextField label="Cabeçalho" value={title} onChange={(e) => setTitle(e.target.value)} variant="outlined" fullWidth /></Grid>
                                            <Grid item xs={12}><TextField label="Rodapé" value={footer} onChange={(e) => setFooter(e.target.value)} variant="outlined" fullWidth /></Grid>
                                            {buttons.map((btn, idx) => (
                                                <Grid item xs={12} key={idx}><TextField label={`Botão ${idx + 1}`} value={btn} onChange={(e) => { const nb = [...buttons]; nb[idx] = e.target.value; setButtons(nb); }} variant="outlined" fullWidth /></Grid>
                                            ))}
                                        </Grid>
                                    </div>
                                )}

                                {values.messageType === "carousel" && (
                                    <div style={{ marginBottom: 20 }}>
                                        <Typography variant="subtitle2" gutterBottom>Cards do Carrossel</Typography>
                                        {carouselCards.map((card, idx) => (
                                            <div key={idx} style={{ border: '1px solid #ccc', padding: 10, marginBottom: 10, borderRadius: 5 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}><TextField label="Título" value={card.title} onChange={(e) => { const nc = [...carouselCards]; nc[idx].title = e.target.value; setCarouselCards(nc); }} variant="outlined" fullWidth size="small" /></Grid>
                                                    <Grid item xs={12}><TextField label="Descrição" value={card.description} onChange={(e) => { const nc = [...carouselCards]; nc[idx].description = e.target.value; setCarouselCards(nc); }} variant="outlined" fullWidth size="small" /></Grid>
                                                    <Grid item xs={12}>
                                                        <Button variant="contained" component="label" fullWidth size="small" style={{ marginBottom: 5 }}>
                                                            {card.imageFile ? card.imageFile.name : (card.mediaUrl ? "Com URL" : "Upload Imagem")}
                                                            <input type="file" accept="image/*" hidden onChange={(e) => { const nc = [...carouselCards]; nc[idx].imageFile = e.target.files[0]; setCarouselCards(nc); }} />
                                                        </Button>
                                                        {!card.imageFile && <TextField label="Ou URL da Imagem" value={card.mediaUrl} onChange={(e) => { const nc = [...carouselCards]; nc[idx].mediaUrl = e.target.value; setCarouselCards(nc); }} variant="outlined" fullWidth size="small" />}
                                                    </Grid>
                                                    <Grid item xs={12}><TextField label="Botão" value={card.button} onChange={(e) => { const nc = [...carouselCards]; nc[idx].button = e.target.value; setCarouselCards(nc); }} variant="outlined" fullWidth size="small" /></Grid>
                                                </Grid>
                                            </div>
                                        ))}
                                        <Button variant="outlined" size="small" onClick={() => setCarouselCards([...carouselCards, { title: "", description: "", mediaUrl: "", button: "" }])}>Adicionar Card</Button>
                                    </div>
                                )}

                                <div style={{ marginBottom: 10 }}>
                                    <FormControlLabel control={<Field as={Checkbox} name="isRecurring" color="primary" />} label="Agendamento Recorrente?" />
                                </div>

                                {values.isRecurring && (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Field as={TextField} label="Intervalo (dias)" name="recursionInterval" type="number" variant="outlined" fullWidth />
                                        <Field as={TextField} label="Fim (opcional)" name="recursionEndDate" type="date" InputLabelProps={{ shrink: true }} variant="outlined" fullWidth />
                                    </div>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("schedules.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {scheduleId ? i18n.t("schedules.buttons.okEdit") : i18n.t("schedules.buttons.okAdd")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default ContactScheduleModal;
